import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MessService } from '../mess.service';
import { NgxSpinnerService } from 'ngx-spinner';
import moment from 'moment';
import { ToastmessageService } from 'src/app/toastmessage.service';
import { ConfirmationMessageDialogComponent } from '../confirmation-message-dialog/confirmation-message-dialog.component';

type CountField = 'studentCount' | 'employeeCount' | 'subStaffCount' | 'visitorsCount';

const MEAL_TYPE_TO_SCENARIO_ID: Record<string, number> = {
  BREAKFAST_QTY: 13,
  LUNCH_QTY: 14,
  SNACKS_QTY: 15,
  DINNER_QTY: 16,
  // Table rows send friendly display names (e.g. "Breakfast") — map those too.
  BREAKFAST: 13,
  LUNCH: 14,
  SNACKS: 15,
  DINNER: 16,
};

@Component({
  selector: 'app-add-meal-request',
  templateUrl: './add-meal-request.component.html',
  styleUrls: ['./add-meal-request.component.scss']
})
export class AddMealRequestComponent implements OnInit {
  kitchens: Array<{ supplyId: number; buildingName: string; subInventoryCode?: string }> = [];
  scenarioTypes: Array<{ id: number; scenarioCode: string; scenarioName: string }> = [];
  /** Rows from `foodRequestHistory` for the selected kitchen + date (used with `existingItems` to disable meal types already requested). */
  foodRequestHistoryRows: any[] = [];

  selectedSupplyId: number | null = null;
  selectedScenarioId: number | null = null;
  buildingName = '';

  studentCount: number | null = null;
  employeeCount: number | null = null;
  subStaffCount: number | null = null;
  visitorsCount: number | null = null;

  studentCountDisplay = '';
  employeeCountDisplay = '';
  subStaffCountDisplay = '';
  visitorsCountDisplay = '';

  /** Campus student count from `getstudentCount` (shown next to Enter Counts). */
  apiStudentCount = 0;
  /** Prevents stacking multiple exceed alerts while the user keeps typing. */
  private studentCountAlertOpen = false;

  constructor(
    private dialogRef: MatDialogRef<AddMealRequestComponent>,
    private matDialog: MatDialog,
    private service: MessService,
    private spinner: NgxSpinnerService,
    private toast: ToastmessageService,
    @Inject(MAT_DIALOG_DATA) public data: {
      date: string;
      existingItems?: any[];
      prefillRow?: any;
      supplyId?: number | string;
      buildingName?: string;
      subInventoryCode?: string;
      editMode?: boolean;
    },
  ) { }

  ngOnInit(): void {
    this.applyInitialKitchenFromParent();
    this.loadStudentCount();
    this.loadKitchens();
    this.loadScenarioTypes();
  }

  get isEditMode(): boolean {
    return this.data?.editMode === true && !!this.data?.prefillRow;
  }

  private loadStudentCount(): void {
    const payrollId = localStorage.getItem('userId');
    if (!payrollId) {
      this.apiStudentCount = 0;
      return;
    }
    this.service.getstudentCount(payrollId).subscribe({
      next: (res: any) => {
        const raw =
          res?.scaitsStudentCount ??
          res?.data?.scaitsStudentCount ??
          res?.studentCount ??
          res?.STUDENT_COUNT ??
          res?.count ??
          res?.data?.studentCount ??
          res?.data?.count ??
          (typeof res === 'number' ? res : null);
        const n = Number(raw);
        this.apiStudentCount = Number.isFinite(n) && n >= 0 ? n : 0;
      },
      error: () => {
        this.apiStudentCount = 0;
      },
    });
  }

  private applyInitialKitchenFromParent(): void {
    const supplyId = Number(this.data?.supplyId ?? NaN);
    if (Number.isFinite(supplyId)) {
      this.selectedSupplyId = supplyId;
    }
    const name = String(this.data?.buildingName ?? '').trim();
    if (name) {
      this.buildingName = name;
    }
  }

  private loadKitchens(): void {
    this.spinner.show();
    this.service.userKitchens().subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        this.kitchens = rows;
        if (this.selectedSupplyId == null && this.kitchens.length > 0) {
          const raw = this.kitchens[0]?.supplyId;
          const n = Number(raw);
          this.selectedSupplyId = raw != null && Number.isFinite(n) ? n : null;
        }
        if (!this.buildingName) {
          this.buildingName = this.selectedKitchenName;
        }
        this.tryApplyPrefillRow();
        this.ensureScenarioSelectionValid();
        this.spinner.hide();
        this.loadFoodRequestHistory();
      },
      error: () => this.spinner.hide(),
    });
  }

  loadFoodRequestHistory(): void {
    if (this.selectedSupplyId == null || !Number.isFinite(Number(this.selectedSupplyId))) {
      this.foodRequestHistoryRows = [];
      this.ensureScenarioSelectionValid();
      return;
    }
    this.spinner.show();
    this.service.foodRequestHistory(this.selectedSupplyId, this.data.date).subscribe({
      next: (res: any) => {
        const raw = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : res);
        const rows = Array.isArray(raw) ? raw : raw != null && typeof raw === 'object' ? [raw] : [];
        this.foodRequestHistoryRows = rows;
        this.ensureScenarioSelectionValid();
        this.spinner.hide();
      },
      error: () => {
        this.foodRequestHistoryRows = [];
        this.ensureScenarioSelectionValid();
        this.spinner.hide();
      },
    });
  }

  private loadScenarioTypes(): void {
    this.service.sceniorTypes().subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        this.scenarioTypes = rows;
        if (this.selectedScenarioId == null && this.scenarioTypes.length > 0 && !this.isEditMode) {
          this.selectedScenarioId = Number(this.scenarioTypes[0]?.id ?? null);
        }
        this.tryApplyPrefillRow();
        this.ensureScenarioSelectionValid();
      },
      error: () => { },
    });
  }

  /** When opened from a table row, bind kitchen, meal type, and counts from that row. */
  private tryApplyPrefillRow(): void {
    const row = this.data?.prefillRow;
    if (!row || typeof row !== 'object') {
      return;
    }

    const kitchenName = String(row.kitchenName ?? row.buildingName ?? this.data?.buildingName ?? '').trim();
    if (kitchenName) {
      this.buildingName = kitchenName;
    }

    const supplyId = Number(row.supplyId ?? this.data?.supplyId ?? NaN);
    if (Number.isFinite(supplyId)) {
      this.selectedSupplyId = supplyId;
    } else if (kitchenName && this.kitchens?.length) {
      const k = this.kitchens.find(x => String(x?.buildingName ?? '').trim() === kitchenName);
      if (k != null && k.supplyId != null) {
        const n = Number(k.supplyId);
        if (Number.isFinite(n)) {
          this.selectedSupplyId = n;
        }
      }
    }

    if (this.scenarioTypes?.length) {
      const sid = Number(row.scenarioId ?? NaN);
      if (Number.isFinite(sid) && this.scenarioTypes.some(x => Number(x?.id) === sid)) {
        this.selectedScenarioId = sid;
      } else {
        const mt = String(row.mealType ?? '').trim().toUpperCase();
        if (mt) {
          const byCode = this.scenarioTypes.find(
            x => String(x?.scenarioCode ?? '').trim().toUpperCase() === mt,
          );
          const byMappedId = MEAL_TYPE_TO_SCENARIO_ID[mt];
          const byId = byMappedId != null
            ? this.scenarioTypes.find(x => Number(x?.id) === byMappedId)
            : undefined;
          const s = byCode ?? byId;
          if (s != null) {
            this.selectedScenarioId = Number(s.id);
          }
        }
      }
    }

    this.setCountField('studentCount', row.studentCount);
    this.setCountField('employeeCount', row.employeeCount);
    this.setCountField('subStaffCount', row.subStaffCount);
    this.setCountField('visitorsCount', row.visitorsCount);
  }

  selectScenario(id: number): void {
    if (this.isScenarioDisabled({ id, scenarioCode: '' } as any)) {
      return;
    }
    if (Number(this.selectedScenarioId) !== Number(id)) {
      if (this.isEditMode) {
        // Row click sends all table rows — switching meal type loads that row's counts.
        const row = this.findExistingRowForScenario(id);
        this.setCountField('studentCount', row?.studentCount);
        this.setCountField('employeeCount', row?.employeeCount);
        this.setCountField('subStaffCount', row?.subStaffCount);
        this.setCountField('visitorsCount', row?.visitorsCount);
      } else {
        this.clearCountInputs();
      }
    }
    this.selectedScenarioId = id;
  }

  /** Find the sent table row (existingItems) matching this scenario for the dialog's kitchen. */
  private findExistingRowForScenario(scenarioId: number): any | null {
    const items = Array.isArray(this.data?.existingItems) ? this.data.existingItems : [];
    if (!items.length || !Number.isFinite(Number(scenarioId))) {
      return null;
    }
    const kitchenName = this.selectedKitchenName;
    const supplyId = Number(this.selectedSupplyId ?? NaN);

    return items.find((r: any) => {
      const rScenarioId = Number(r?.scenarioId ?? NaN);
      const rMealType = String(r?.mealType ?? r?.scenarioCode ?? '').trim().toUpperCase();
      const rMappedScenarioId = MEAL_TYPE_TO_SCENARIO_ID[rMealType];
      const scenarioMatch =
        (Number.isFinite(rScenarioId) && rScenarioId === Number(scenarioId)) ||
        (Number.isFinite(rMappedScenarioId) && rMappedScenarioId === Number(scenarioId));
      if (!scenarioMatch) {
        return false;
      }
      const rSupplyId = Number(r?.supplyId ?? NaN);
      if (Number.isFinite(rSupplyId) && Number.isFinite(supplyId)) {
        return rSupplyId === supplyId;
      }
      const rKitchen = String(r?.kitchenName ?? r?.buildingName ?? '').trim();
      return kitchenName === '-' || rKitchen === '' || rKitchen === kitchenName;
    }) ?? null;
  }

  private clearCountInputs(): void {
    this.studentCount = null;
    this.employeeCount = null;
    this.subStaffCount = null;
    this.visitorsCount = null;
    this.studentCountDisplay = '';
    this.employeeCountDisplay = '';
    this.subStaffCountDisplay = '';
    this.visitorsCountDisplay = '';
  }

  private setCountField(field: CountField, value: unknown): void {
    const n = Number(value ?? NaN);
    if (!Number.isFinite(n) || n < 0) {
      this[field] = null;
      this.getCountDisplayField(field).set('');
      return;
    }
    this[field] = n;
    this.getCountDisplayField(field).set(this.formatCount(n));
  }

  private getCountDisplayField(field: CountField): { set: (value: string) => void } {
    switch (field) {
      case 'studentCount':
        return { set: (value) => (this.studentCountDisplay = value) };
      case 'employeeCount':
        return { set: (value) => (this.employeeCountDisplay = value) };
      case 'subStaffCount':
        return { set: (value) => (this.subStaffCountDisplay = value) };
      default:
        return { set: (value) => (this.visitorsCountDisplay = value) };
    }
  }

  onCountInput(field: CountField, event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = String(input.value ?? '').replace(/\D/g, '');
    if (!digitsOnly) {
      this[field] = null;
      this.getCountDisplayField(field).set('');
      input.value = '';
      return;
    }
    const n = Number(digitsOnly);
    if (!Number.isFinite(n) || n < 0) {
      this.restoreCountInput(field, input);
      return;
    }
    const truncated = Math.trunc(n);
    if (field === 'studentCount' && truncated > this.apiStudentCount) {
      this.restoreCountInput(field, input);
      this.showStudentCountExceededAlert();
      return;
    }
    this[field] = truncated;
    const formatted = this.formatCount(truncated);
    this.getCountDisplayField(field).set(formatted);
    input.value = formatted;
  }

  onCountBlur(field: CountField, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (field === 'studentCount') {
      const typed = Number(String(input.value ?? '').replace(/\D/g, ''));
      if (Number.isFinite(typed) && typed > this.apiStudentCount) {
        this.restoreCountInput(field, input);
        this.showStudentCountExceededAlert();
        return;
      }
    }
    this.restoreCountInput(field, input);
  }

  onStudentCountKeydown(event: KeyboardEvent): void {
    if (event.key === '.') {
      event.preventDefault();
      return;
    }
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const next = `${input.value.slice(0, start)}${event.key}${input.value.slice(end)}`.replace(/\D/g, '');
    if (!next) {
      return;
    }
    const nextN = Number(next);
    if (Number.isFinite(nextN) && nextN > this.apiStudentCount) {
      event.preventDefault();
      this.restoreCountInput('studentCount', input);
      this.showStudentCountExceededAlert();
    }
  }

  /** Keep the last accepted count in the input (do not display an over-limit value). */
  private restoreCountInput(field: CountField, input?: HTMLInputElement): void {
    const value = this[field];
    const formatted = value == null ? '' : this.formatCount(value);
    this.getCountDisplayField(field).set(formatted);
    if (input) {
      input.value = formatted;
    }
  }

  /** Alert when Students exceed campus `scaitsStudentCount`. */
  private showStudentCountExceededAlert(): void {
    if (this.studentCountAlertOpen) {
      return;
    }
    this.studentCountAlertOpen = true;
    const ref = this.matDialog.open(ConfirmationMessageDialogComponent, {
      width: '400px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      data: {
        title: 'Student Count Exceeded',
        message: `Student count cannot be more than ${this.formatCount(this.apiStudentCount)}.`,
        confirmLabel: 'OK',
        confirmResult: 'ok',
        singleAction: true,
      },
    });
    ref.afterClosed().subscribe(() => {
      this.studentCountAlertOpen = false;
      this.studentCountDisplay = this.studentCount == null ? '' : this.formatCount(this.studentCount);
    });
  }

  formatCount(value: number | null | undefined): string {
    if (value == null || Number.isNaN(Number(value))) {
      return '';
    }
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(Number(value));
  }

  get totalHeads(): number {
    return (
      Number(this.studentCount ?? 0) +
      Number(this.employeeCount ?? 0) +
      Number(this.subStaffCount ?? 0) +
      Number(this.visitorsCount ?? 0)
    );
  }

  get selectedKitchenName(): string {
    if (this.buildingName) {
      return this.buildingName;
    }
    const id = Number(this.selectedSupplyId ?? NaN);
    const k = this.kitchens.find(x => Number(x?.supplyId) === id);
    return String(k?.buildingName ?? '').trim() || '-';
  }

  get selectedScenarioName(): string {
    const id = Number(this.selectedScenarioId ?? NaN);
    const s = this.scenarioTypes.find(x => Number(x?.id) === id);
    return String(s?.scenarioName ?? '').trim() || '-';
  }

  isScenarioDisabled(s: { id: number; scenarioCode: string }): boolean {
    if (this.isEditMode) {
      // Enable the row's own meal type plus any meal type present in the sent table data.
      if (Number(s?.id) === Number(this.selectedScenarioId)) {
        return false;
      }
      return !this.findExistingRowForScenario(Number(s?.id));
    }

    const kitchenName = this.selectedKitchenName;
    const scenarioId = Number(s?.id ?? NaN);
    const scenarioCode = String(s?.scenarioCode ?? '').trim();

    const fromDialog = Array.isArray(this.data?.existingItems) ? this.data.existingItems : [];
    const fromHistory = Array.isArray(this.foodRequestHistoryRows) ? this.foodRequestHistoryRows : [];
    const items = [...fromDialog, ...fromHistory];
    if (!items.length) return false;

    return items.some((r: any) => {
      const rKitchen = String(r?.kitchenName ?? r?.buildingName ?? '').trim();
      const rScenarioId = Number(r?.scenarioId ?? NaN);
      const rMealType = String(r?.mealType ?? r?.scenarioCode ?? '').trim().toUpperCase();
      const rMappedScenarioId = MEAL_TYPE_TO_SCENARIO_ID[rMealType];

      const kitchenMatch = kitchenName !== '-' && rKitchen !== '' && rKitchen === kitchenName;
      const scenarioMatch =
        (Number.isFinite(rScenarioId) && Number.isFinite(scenarioId) && rScenarioId === scenarioId) ||
        (Number.isFinite(rMappedScenarioId) && Number.isFinite(scenarioId) && rMappedScenarioId === scenarioId) ||
        (scenarioCode !== '' && rMealType === scenarioCode.toUpperCase());

      return kitchenMatch && scenarioMatch;
    });
  }

  private ensureScenarioSelectionValid(): void {
    if (!this.scenarioTypes?.length) return;
    if (this.isEditMode && this.selectedScenarioId != null) {
      return;
    }
    const cur = this.selectedScenarioId;
    const curObj = this.scenarioTypes.find(x => Number(x?.id) === Number(cur ?? NaN));
    if (curObj && !this.isScenarioDisabled(curObj as any)) {
      return;
    }
    const firstEnabled = this.scenarioTypes.find(x => !this.isScenarioDisabled(x as any));
    this.selectedScenarioId = firstEnabled ? Number(firstEnabled.id) : null;
  }

  submitRequest(): void {
    const validation = this.validateRequest();
    if (!validation.valid) {
      this.toast.showError(validation.message ?? 'Please complete all fields.');
      return;
    }

    const mealName = this.selectedScenarioName !== '-'
      ? this.selectedScenarioName
      : 'meal';
    const confirmRef = this.matDialog.open(ConfirmationMessageDialogComponent, {
      width: '400px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      data: {
        title: this.isEditMode ? 'Update Meal Request' : 'Submit Meal Request',
        message: this.isEditMode
          ? `Do you want update ${mealName} meal request.`
          : `Do you want submit ${mealName} meal request.`,
        confirmLabel: this.isEditMode ? 'Update' : 'Submit',
        confirmResult: 'submit',
      },
    });
    confirmRef.afterClosed().subscribe((result) => {
      if (result === 'submit' && validation.payload) {
        this.confirmSubmitRequest(validation.payload);
      }
    });
  }

  private validateRequest():
    | { valid: true; payload: Record<string, unknown> }
    | { valid: false; message: string } {
    const requestDate = String(this.data?.date ?? '').trim() || moment().format('DD MMM YYYY');
    const payload = {
      supplyId: Number(this.selectedSupplyId ?? 0),
      scenarioId: Number(this.selectedScenarioId ?? 0),
      requestDate: requestDate,
      studentCount: Number(this.studentCount ?? 0),
      employeeCount: Number(this.employeeCount ?? 0),
      subStaffCount: Number(this.subStaffCount ?? 0),
      visitorsCount: Number(this.visitorsCount ?? 0),
      scaitsStudentCount: Number(this.apiStudentCount ?? 0),
    };
    const heads =
      Number(payload.studentCount ?? 0) +
      Number(payload.employeeCount ?? 0) +
      Number(payload.subStaffCount ?? 0) +
      Number(payload.visitorsCount ?? 0);
    if (Number(payload.studentCount ?? 0) > this.apiStudentCount) {
      return {
        valid: false,
        message: `Student count cannot be more than ${this.formatCount(this.apiStudentCount)}.`,
      };
    }
    if (!heads || heads <= 0) {
      return { valid: false, message: 'Please enter Student / Employee / SubStaff / Visitors count before submitting.' };
    }
    return { valid: true, payload };
  }

  private confirmSubmitRequest(payload: Record<string, unknown>): void {
    this.spinner.show();
    this.service.submitFoodRequest(payload).subscribe({
      next: (res: any) => {
        if (!res) {
          this.spinner.hide();
          return
        }
        this.toast.showSuccess(res.message);
        this.spinner.hide();
        this.dialogRef.close(res ?? payload);
      },
      error: () => this.spinner.hide(),
    });
  }

  getScenarioIcon(scenarioName: string): string {
    const key = (scenarioName || '').toLowerCase();
    if (key.includes('breakfast')) {
      return 'free_breakfast';
    }
    if (key.includes('lunch')) {
      return 'lunch_dining';
    }
    if (key.includes('snack')) {
      return 'bakery_dining';
    }
    if (key.includes('dinner')) {
      return 'dinner_dining';
    }
    return 'restaurant';
  }

  close(): void {
    this.dialogRef.close();
  }
}
